<script setup lang="ts">
/**
 * Список встреч — левая колонка экрана «Встречи» (макеты 10-1…10-4).
 *
 * Раскладка мессенджера: слева разговоры, справа их содержимое. Встреча — это и
 * есть разговор, и искать вчерашний протокол человек идёт туда же, где искал бы
 * вчерашнюю переписку.
 *
 * Первой строкой — личные заметки: они не встреча, но лежат в той же ленте и
 * пишутся тем же полем. Отдельная страница ради них означала бы два места, где
 * ищут одну и ту же мысль.
 */
import { ChevronDown, ChevronRight, NotebookPen } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { isMeetingDone } from '../api'

import MeetingRow from './MeetingRow.vue'

import type { MeetingSession } from '../api'

const props = defineProps<{
  meetings: MeetingSession[]
  /** Открытая встреча; пусто — открыты личные заметки */
  selectedId: string
  /** Открытая серия: её записи показаны вместо ленты одной встречи */
  selectedSeriesId?: string
  isLoading: boolean
}>()

const emit = defineEmits<{ select: [sessionId: string]; selectSeries: [seriesRootId: string] }>()

const { t } = useI18n()

/**
 * Секции списка (макет 12-1).
 *
 * Идущая всегда сверху: за ней и приходят. Дальше сегодняшние и ближайшие —
 * в них пишут повестку до созвона, — и только потом архив.
 */
const live = computed(() => props.meetings.filter((meeting) => meeting.active).map(of))

/** Одиночная строка: у идущей встречи серии на экране нет — она одна */
const of = (meeting: MeetingSession): Row => ({ meeting, rest: [] })

const isToday = (iso: string) => {
  const date = new Date(iso)
  const now = new Date()

  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

interface Row {
  meeting: MeetingSession
  rest: MeetingSession[]
}

/** Корень серии: им собирается история, пережившая разрыв «эта и следующие» */
const rootOf = (meeting: MeetingSession) => meeting.seriesRootId || meeting.seriesId

/**
 * Серия — одна строка списка, а не десять одинаковых (макет 12-20).
 *
 * Живой её считает сервер: по странице списка не отличить «серия закончилась»
 * от «её следующий созвон за пределами страницы», и серия с одним прошедшим
 * созвоном уезжала в «Прошедшие» целиком (DEV-2183).
 */
const seriesRows = computed(() => {
  const byRoot = new Map<string, MeetingSession[]>()

  for (const meeting of props.meetings) {
    const root = rootOf(meeting)
    if (!root || !meeting.seriesActive) continue

    byRoot.set(root, [...(byRoot.get(root) ?? []), meeting])
  }

  const rows: Row[] = []

  for (const items of byRoot.values()) {
    const sorted = [...items].sort((first, second) =>
      first.scheduledStart.localeCompare(second.scheduledStart),
    )

    /*
    Лицом строки стоит ближайший созвон, который ещё предстоит: у него пишут
    повестку и по нему подключаются. Просроченный SCHEDULED висит так три часа,
    пока сосед не поставит «без записи», и лицом серии быть не должен; идущий
    созвон показан отдельной группой «Идёт».
    */
    const face = sorted.find(isNext) ?? sorted[sorted.length - 1]
    if (!face) continue

    rows.push({ meeting: face, rest: sorted.filter((meeting) => meeting.id !== face.id) })
  }

  return rows
})

/** Созвон, который ещё предстоит: не идёт, не отменён и не позади */
const isNext = (meeting: MeetingSession) =>
  !meeting.active &&
  !!meeting.scheduledEnd &&
  new Date(meeting.scheduledEnd).getTime() >= Date.now() &&
  meeting.meetingStatus !== 'CANCELLED'

/** Корни живых серий: их экземпляры не повторяются в других группах */
const liveRoots = computed(() => new Set(seriesRows.value.map((row) => rootOf(row.meeting))))

const scheduled = computed(() =>
  props.meetings
    .filter((meeting) => !meeting.active && !isMeetingDone(meeting))
    .filter((meeting) => !liveRoots.value.has(rootOf(meeting)) || isToday(meeting.scheduledStart))
    .sort((first, second) => first.scheduledStart.localeCompare(second.scheduledStart)),
)

/*
Сегодняшний созвон живой серии показан и в «Сегодня» (решение владельца
19.09.2026): его ищут глазами там, где стоят остальные встречи дня. Дальше по
списку серия не повторяется.
*/
const today = computed(() => scheduled.value.filter((meeting) => isToday(meeting.scheduledStart)).map(of))
const soon = computed(() =>
  rowsOf(scheduled.value.filter((meeting) => !isToday(meeting.scheduledStart))),
)

const rowsOf = (items: MeetingSession[]): Row[] => {
  const rows: Row[] = []
  const bySeries = new Map<string, Row>()

  for (const meeting of items) {
    const root = rootOf(meeting)
    const row = root ? bySeries.get(root) : undefined

    if (row) {
      row.rest.push(meeting)

      continue
    }

    const fresh: Row = { meeting, rest: [] }

    if (root) bySeries.set(root, fresh)

    rows.push(fresh)
  }

  return rows
}

/** Строка серии: в группе «Серии» и мёртвая серия в «Прошедших» */
const isSeriesRow = (group: string, row: Row) =>
  group === 'series' || (group === 'past' && !!rootOf(row.meeting) && row.rest.length > 0)

/** Раскрытые серии: развёрнутая остаётся такой, пока её не свернут */
const opened = ref(new Set<string>())

const toggleSeries = (seriesId: string) => {
  const next = new Set(opened.value)

  if (!next.delete(seriesId)) next.add(seriesId)

  opened.value = next
}

/*
В «Прошедших» — завершённые встречи и мёртвые серии одной строкой. Живая серия
сюда не попадает, даже если её прошлых созвонов десяток: она стоит выше,
в своей группе.
*/
const past = computed(() =>
  rowsOf(
    props.meetings
      .filter((meeting) => !meeting.active && isMeetingDone(meeting))
      .filter((meeting) => !liveRoots.value.has(rootOf(meeting))),
  ),
)

</script>

<template>
  <aside class="mlist">
    <!-- Класс строки общий с встречами: личные заметки лежат в том же списке
         и выделяются так же, хотя встречей и не являются. -->
    <button
      type="button"
      class="mlist__item mlist__personal"
      :class="{
        'mlist__item--selected': !selectedId && !selectedSeriesId,
        'mlist__personal--selected': !selectedId && !selectedSeriesId,
      }"
      @click="emit('select', '')"
    >
      <NotebookPen :size="16" class="mlist__icon" aria-hidden="true" />

      <span class="mlist__body">
        <span class="mlist__title">{{ t('meetings.personal.title') }}</span>
        <span class="mlist__sub">{{ t('meetings.personal.hint') }}</span>
      </span>
    </button>

    <p v-if="isLoading && !meetings.length" class="mlist__empty">{{ t('meetings.loading') }}</p>

    <template
      v-for="group in [
        { key: 'live', label: '', items: live },
        { key: 'series', label: t('meetings.groups.series'), items: seriesRows },
        { key: 'today', label: t('meetings.groups.today'), items: today },
        { key: 'soon', label: t('meetings.groups.soon'), items: soon },
        { key: 'past', label: t('meetings.groups.past'), items: past },
      ]"
      :key="group.key"
    >
      <p v-if="group.label && group.items.length" class="mlist__group">{{ group.label }}</p>

      <template v-for="row in group.items" :key="row.meeting.id">
        <!-- Строка серии ведёт в её записи, строка созвона — в его ленту:
             за «что было на этих стендапах» и «что сегодня» приходят
             по-разному (DEV-2185). -->
        <MeetingRow
          :meeting="row.meeting"
          :is-selected="
            isSeriesRow(group.key, row)
              ? selectedSeriesId === rootOf(row.meeting)
              : selectedId === row.meeting.id
          "
          :is-series="isSeriesRow(group.key, row)"
          @select="
            isSeriesRow(group.key, row)
              ? emit('selectSeries', rootOf(row.meeting))
              : emit('select', row.meeting.id)
          "
        />

        <!-- Серия свёрнута в свой ближайший созвон: развёрнутая заняла бы
             список собой, а в нём есть и другие встречи (макет 12-7). -->
        <template v-if="row.rest.length">
          <button
            type="button"
            class="mlist__more"
            @click="toggleSeries(row.meeting.seriesRootId || row.meeting.seriesId)"
          >
            <ChevronDown
              v-if="opened.has(row.meeting.seriesRootId || row.meeting.seriesId)"
              :size="12"
              aria-hidden="true"
            />
            <ChevronRight v-else :size="12" aria-hidden="true" />
            {{ t('meetings.series.more', { count: row.rest.length }) }}
          </button>

          <template v-if="opened.has(row.meeting.seriesRootId || row.meeting.seriesId)">
            <MeetingRow
              v-for="instance in row.rest"
              :key="instance.id"
              :meeting="instance"
              :is-selected="selectedId === instance.id"
              is-nested
              @select="emit('select', instance.id)"
            />
          </template>
        </template>
      </template>
    </template>

    <p v-if="!isLoading && !meetings.length" class="mlist__empty">{{ t('meetings.list.empty') }}</p>
  </aside>
</template>

<style scoped>
.mlist {
  display: flex;
  flex: 0 0 300px;
  flex-direction: column;
  gap: 2px;
  width: 300px;
  /* Список скроллится внутри себя: длинная история созвонов не должна тянуть
     вниз ленту справа — ровно та беда, из-за которой заметки уехали с плана. */
  min-height: 0;
  overflow-y: auto;
  padding-right: 8px;
  border-right: 1px solid var(--color-border);
}

.mlist__personal {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.mlist__personal:hover {
  background: var(--color-bg-muted);
}

.mlist__personal--selected {
  background: var(--color-primary-tint);
}

.mlist__icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--color-text-muted);
}

.mlist__personal--selected .mlist__icon {
  color: var(--color-primary);
}

.mlist__body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.mlist__title {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 18px;
}

.mlist__sub {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mlist__group {
  margin: 10px 10px 2px;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  line-height: 14px;
  text-transform: uppercase;
}

/* Раскрытие серии — строкой под ней, а не кнопкой в углу: её ищут глазами
   там, где кончился ближайший созвон. */
.mlist__more {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 26px;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--font-size-overline);
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}

.mlist__more:hover {
  color: var(--color-text-primary);
}

.mlist__empty {
  margin: 8px 10px;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}
</style>
